import {
  useLocation as useRouterLocation,
  useNavigate as useRouterNavigate,
} from 'react-router-dom';

import { logger } from '@/utils/logger';

/**
 * Safe wrapper for useLocation that handles router context errors gracefully
 * Use this in components that might be rendered outside of router context
 */
export const useLocationSafe = () => {
  try {
    return useRouterLocation();
  } catch (error) {
    logger.warn('useLocation called outside router context:', error);
    // Return a mock location object that matches the Location interface
    return {
      pathname: '/',
      search: '',
      hash: '',
      state: null,
      key: 'default',
    };
  }
};

/**
 * Safe wrapper for useNavigate that handles router context errors gracefully
 * Use this in components that might be rendered outside of router context
 */
export const useNavigateSafe = () => {
  try {
    return useRouterNavigate();
  } catch (error) {
    logger.warn('useNavigate called outside router context:', error);
    // Return a function that uses window.location for navigation
    return (to: string | number, _options?: any) => {
      if (typeof to === 'string') {
        // Internal navigations only — reject external URLs to prevent
        // open redirects if a caller ever forwards untrusted user input.
        if (to.startsWith('/') && !to.startsWith('//')) {
          window.location.href = to;
        } else {
          logger.warn('useNavigateSafe blocked external/relative target:', to);
        }
      } else if (typeof to === 'number') {
        window.history.go(to);
      }
    };
  }
};

// Re-export the safe versions as the default ones to use
export { useLocationSafe as useLocation, useNavigateSafe as useNavigate };
